/** 요소가 레이아웃에 잡혀 보이는지 (v-show=false / display:none 제외) */
export function isElementLaidOut(el: HTMLElement | null | undefined): boolean {
  if (!el) return false
  const style = window.getComputedStyle(el)
  if (style.display === 'none' || style.visibility === 'hidden') return false
  const rect = el.getBoundingClientRect()
  return rect.width > 0 || rect.height > 0
}

/** 가장 가까운 세로 스크롤 컨테이너 */
export function getScrollParent(el: HTMLElement | null): HTMLElement | null {
  let node = el?.parentElement ?? null
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node)
    const overflowY = style.overflowY
    const canScroll =
      (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
      node.scrollHeight > node.clientHeight + 1
    if (canScroll) return node
    node = node.parentElement
  }
  return null
}

export interface ScrollIntoContainerOptions {
  behavior?: ScrollBehavior
  /** start: 위쪽 여유 / center: 가운데 / nearest: 안 보이면만 */
  block?: 'start' | 'center' | 'nearest'
  /** start 기준 상단 여백(px). sticky 헤더가 있으면 키운다. */
  offsetTop?: number
  offsetBottom?: number
  /** 지정하면 getScrollParent 대신 이 컨테이너를 사용 */
  container?: HTMLElement | null
}

/**
 * overflow 스크롤 영역 안에서 대상이 보이게 이동.
 * (모달·사이드 패널처럼 창 전체가 아닌 내부 스크롤에 필수)
 */
export function scrollIntoContainer(
  target: HTMLElement | null | undefined,
  options: ScrollIntoContainerOptions = {},
): void {
  if (!target || !isElementLaidOut(target)) return

  const behavior = options.behavior ?? 'smooth'
  const block = options.block ?? 'start'
  const offsetTop = options.offsetTop ?? 12
  const offsetBottom = options.offsetBottom ?? 12
  const container = options.container ?? getScrollParent(target)

  if (!container) {
    target.scrollIntoView({ behavior, block: block === 'nearest' ? 'nearest' : block })
    return
  }

  const cRect = container.getBoundingClientRect()
  const tRect = target.getBoundingClientRect()
  const current = container.scrollTop
  let next = current

  if (block === 'center') {
    /* sticky 상단을 제외한 보이는 영역의 가운데 */
    const usable = Math.max(0, container.clientHeight - offsetTop - offsetBottom)
    next = current + (tRect.top - cRect.top) - offsetTop - usable / 2 + tRect.height / 2
  } else if (block === 'nearest') {
    const topLimit = cRect.top + offsetTop
    const bottomLimit = cRect.bottom - offsetBottom
    if (tRect.top >= topLimit && tRect.bottom <= bottomLimit) return
    if (tRect.top < topLimit) {
      next = current + (tRect.top - cRect.top) - offsetTop
    } else {
      next = current + (tRect.bottom - cRect.bottom) + offsetBottom
    }
  } else {
    next = current + (tRect.top - cRect.top) - offsetTop
  }

  const max = Math.max(0, container.scrollHeight - container.clientHeight)
  container.scrollTo({ top: Math.min(max, Math.max(0, next)), behavior })
}

/** v-show·단계 전환 직후, 레이아웃될 때까지 기다렸다가 스크롤 */
export async function scrollWhenReady(
  getTarget: () => HTMLElement | null | undefined,
  options: ScrollIntoContainerOptions = {},
  timeoutMs = 500,
): Promise<HTMLElement | null> {
  const start = performance.now()
  let el: HTMLElement | null = null
  while (performance.now() - start < timeoutMs) {
    el = getTarget() ?? null
    if (isElementLaidOut(el)) break
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
  }
  if (!isElementLaidOut(el)) return el
  /* 한 프레임 더 기다려 스크롤 높이 확정 */
  await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()))
  scrollIntoContainer(el, options)
  return el
}

/** @deprecated scrollWhenReady 사용 */
export function scrollIntoContainerAfterPaint(
  getTarget: () => HTMLElement | null | undefined,
  options?: ScrollIntoContainerOptions,
  delayMs = 50,
): void {
  window.requestAnimationFrame(() => {
    window.setTimeout(() => {
      void scrollWhenReady(getTarget, options, Math.max(delayMs, 80))
    }, delayMs)
  })
}
